import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormLabel,
  ClickAwayListener,
  Collapse,
} from '@mui/material';
import { Close, KeyboardArrowDown } from '@mui/icons-material';

function ProjectSelector({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select projects...',
  label = 'Projects',
}) {
  const [open, setOpen] = useState(false);

  const handleToggleOption = (option) => {
    const isSelected = selected.some((item) => item.id === option.id);

    if (isSelected) {
      const newSelected = selected.filter((item) => item.id !== option.id);
      onChange(newSelected);
    } else {
      onChange([...selected, option]);
    }
  };

  const handleRemoveChip = (event, optionToRemove) => {
    event.stopPropagation();
    const newSelected = selected.filter((item) => item.id !== optionToRemove.id);
    onChange(newSelected);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleInputClick = () => {
    setOpen(!open);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <FormLabel
          sx={{
            color: '#293855',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px',
            display: 'block',
          }}
        >
          {label}
        </FormLabel>

        {/* Input Field with chips inside */}
        <Box
          onClick={handleInputClick}
          sx={{
            minHeight: '56px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '8px 12px',
            cursor: 'pointer',
            backgroundColor: 'white',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            position: 'relative',
            '&:hover': {
              borderColor: '#1976d2',
            },
            '&:focus-within': {
              borderColor: '#1976d2',
              outline: '2px solid rgba(25, 118, 210, 0.2)',
            },
          }}
        >
          {/* Selected items as chips - Updated styling */}
          {selected.map((option) => (
            <Chip
              key={option.id}
              label={option.name}
              size="small"
              deleteIcon={<Close sx={{ fontSize: '18px !important' }} />}
              onDelete={(event) => handleRemoveChip(event, option)}
              sx={{
                height: '32px',
                backgroundColor: '#f5f5f5',
                color: '#333',
                fontSize: '14px',
                fontWeight: '400',
                border: 'none',
                borderRadius: '16px',
                '& .MuiChip-label': {
                  padding: '0 8px',
                },
                '& .MuiChip-deleteIcon': {
                  color: '#666',
                  fontSize: '18px',
                  margin: '0 4px 0 0',
                  '&:hover': {
                    color: '#333',
                  },
                },
              }}
            />
          ))}

          {/* Placeholder text when no items selected */}
          {selected.length === 0 && (
            <Box sx={{ color: '#999', fontSize: '16px', flexGrow: 1 }}>
              {placeholder}
            </Box>
          )}

          {/* Dropdown Arrow */}
          <KeyboardArrowDown
            sx={{
              color: '#7D8698',
              position: 'absolute',
              right: '12px',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </Box>

        {/* Dropdown List */}
        <Collapse in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              backgroundColor: 'white',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              maxHeight: '240px',
              overflow: 'auto',
              mt: 0.5,
            }}
          >
            <List sx={{ padding: 0 }}>
              {options.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No projects available"
                    sx={{ color: '#7D8698' }}
                  />
                </ListItem>
              ) : (
                options.map((option) => {
                  const isSelected = selected.some((item) => item.id === option.id);

                  return (
                    <ListItem
                      key={option.id}
                      button
                      onClick={() => handleToggleOption(option)}
                      sx={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#F4F6F7' : 'transparent',
                        color: '#333',
                        '&:hover': {
                          backgroundColor: isSelected ? '#E8EAED' : '#f5f5f5',
                        },
                      }}
                    >
                      <ListItemText
                        primary={option.name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '14px',
                            fontWeight: '400',
                          },
                        }}
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          </Box>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
}

ProjectSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
};

ProjectSelector.defaultProps = {
  options: [],
  selected: [],
  placeholder: 'Select projects...',
  label: 'Projects',
};

export default ProjectSelector;
